/**
 * ResponsiveLayout - Stories 24.75-24.80
 * Responsive layout components for mobile-first design
 */

import React, { useState, useEffect, createContext, useContext } from 'react';
import {
  Menu, X, ChevronLeft, ChevronRight, Home, Settings,
  HelpCircle, Bell, User, Search
} from 'lucide-react';

// Breakpoint hook
export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const breakpoints = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
};

export const useBreakpoint = (): Breakpoint => {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>('lg');

  useEffect(() => {
    const getBreakpoint = (): Breakpoint => {
      const width = window.innerWidth;
      if (width >= breakpoints['2xl']) return '2xl';
      if (width >= breakpoints.xl) return 'xl';
      if (width >= breakpoints.lg) return 'lg';
      if (width >= breakpoints.md) return 'md';
      if (width >= breakpoints.sm) return 'sm';
      return 'xs';
    };

    const handleResize = () => setBreakpoint(getBreakpoint());
    handleResize(); // Initial check
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return breakpoint;
};

export const useIsMobile = (): boolean => {
  const breakpoint = useBreakpoint();
  return breakpoint === 'xs' || breakpoint === 'sm';
};

export const useIsTablet = (): boolean => {
  const breakpoint = useBreakpoint();
  return breakpoint === 'md';
};

export const useIsDesktop = (): boolean => {
  const breakpoint = useBreakpoint();
  return breakpoint === 'lg' || breakpoint === 'xl' || breakpoint === '2xl';
};

// Responsive sidebar context
interface SidebarContextType {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
}

const SidebarContext = createContext<SidebarContextType>({
  isOpen: true,
  toggle: () => {},
  open: () => {},
  close: () => {}
});

export const useSidebar = () => useContext(SidebarContext);

// Main responsive layout
interface ResponsiveLayoutProps {
  children: React.ReactNode;
  sidebar?: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  sidebarWidth?: number;
  collapsedSidebarWidth?: number;
  showMobileNav?: boolean;
}

export const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({
  children,
  sidebar,
  header,
  footer,
  sidebarWidth = 280,
  collapsedSidebarWidth = 64,
  showMobileNav = true
}) => {
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile]);

  const sidebarContext: SidebarContextType = {
    isOpen: sidebarOpen,
    toggle: () => setSidebarOpen(prev => !prev),
    open: () => setSidebarOpen(true),
    close: () => setSidebarOpen(false)
  };

  const currentSidebarWidth = sidebarCollapsed ? collapsedSidebarWidth : sidebarWidth;

  return (
    <SidebarContext.Provider value={sidebarContext}>
      <div className="min-h-screen bg-gray-50">
        {/* Mobile header */}
        {isMobile && header && (
          <header className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200">
            <div className="flex items-center justify-between px-4 h-14">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <Menu className="w-5 h-5" />
              </button>
              {header}
            </div>
          </header>
        )}

        {/* Sidebar */}
        {sidebar && (
          <>
            {/* Mobile overlay */}
            {isMobile && sidebarOpen && (
              <div
                className="fixed inset-0 z-40 bg-black/50"
                onClick={() => setSidebarOpen(false)}
              />
            )}

            {/* Sidebar panel */}
            <aside
              className={`fixed top-0 left-0 z-50 h-full bg-white border-r border-gray-200 transition-all duration-300 ${
                isMobile
                  ? sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                  : 'translate-x-0'
              }`}
              style={{ width: isMobile ? sidebarWidth : currentSidebarWidth }}
            >
              {/* Sidebar header */}
              <div className="flex items-center justify-between h-14 px-4 border-b border-gray-200">
                {!sidebarCollapsed && (
                  <span className="font-semibold text-gray-800">Cosellus</span>
                )}
                {isMobile ? (
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={() => setSidebarCollapsed(prev => !prev)}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                  >
                    {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                  </button>
                )}
              </div>

              {/* Sidebar content */}
              <div className="flex-1 overflow-y-auto p-4">
                {sidebar}
              </div>
            </aside>
          </>
        )}

        {/* Main content */}
        <main
          className={`transition-all duration-300 ${
            isMobile ? 'pt-14' : ''
          }`}
          style={{
            marginLeft: sidebar && !isMobile ? currentSidebarWidth : 0
          }}
        >
          {/* Desktop header */}
          {!isMobile && header && (
            <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
              <div className="flex items-center justify-between px-6 h-14">
                {header}
              </div>
            </header>
          )}

          {/* Content */}
          <div className="p-4 md:p-6">
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <footer className="p-4 md:p-6 border-t border-gray-200">
              {footer}
            </footer>
          )}
        </main>

        {/* Mobile bottom navigation */}
        {isMobile && showMobileNav && (
          <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200">
            <div className="flex items-center justify-around h-16">
              <MobileNavItem icon={Home} label="Home" active />
              <MobileNavItem icon={Search} label="Search" />
              <MobileNavItem icon={Bell} label="Alerts" />
              <MobileNavItem icon={User} label="Profile" />
            </div>
          </nav>
        )}
      </div>
    </SidebarContext.Provider>
  );
};

// Mobile nav item
interface MobileNavItemProps {
  icon: React.ElementType;
  label: string;
  active?: boolean;
  onClick?: () => void;
}

const MobileNavItem: React.FC<MobileNavItemProps> = ({
  icon: Icon,
  label,
  active = false,
  onClick
}) => (
  <button
    onClick={onClick}
    className={`flex flex-col items-center justify-center gap-1 w-16 ${
      active ? 'text-blue-600' : 'text-gray-500'
    }`}
  >
    <Icon className="w-5 h-5" />
    <span className="text-xs">{label}</span>
  </button>
);

// Responsive card grid
interface ResponsiveGridProps {
  children: React.ReactNode;
  minCardWidth?: number;
  gap?: number;
  className?: string;
}

export const ResponsiveGrid: React.FC<ResponsiveGridProps> = ({
  children,
  minCardWidth = 280,
  gap = 16,
  className = ''
}) => (
  <div
    className={className}
    style={{
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(${minCardWidth}px, 1fr))`,
      gap: `${gap}px`
    }}
  >
    {children}
  </div>
);

// Responsive show/hide
interface ShowProps {
  children: React.ReactNode;
  above?: Breakpoint;
  below?: Breakpoint;
}

export const Show: React.FC<ShowProps> = ({ children, above, below }) => {
  const breakpoint = useBreakpoint();
  const currentWidth = breakpoints[breakpoint];

  if (above && currentWidth < breakpoints[above]) return null;
  if (below && currentWidth >= breakpoints[below]) return null;

  return <>{children}</>;
};

export default {
  ResponsiveLayout,
  ResponsiveGrid,
  Show,
  useBreakpoint,
  useIsMobile,
  useIsTablet,
  useIsDesktop,
  useSidebar
};
